import type * as React from "react";
import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import type { IMedia } from "src/model/unityObject";
import Player360 from "./Player360";
import Model3dViewerWeb from "./Model3dViewerWeb";
import InteractiveViewer360 from "../../statsNew/components/gazeHeatmap/InteractiveViewer360";
import VideoControls from "../../statsNew/components/gazeHeatmap/VideoControls";
import { prettifyFileSizeInBytes } from "src/common/util/prettifyFileSizeInBytes";
import type { ILanguage } from "src/model/model";
import { languageConfig } from "src/common/util/langConfig";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import { prettifyMediaType } from "src/common/util/prettifyMediaType";
import { useTranslation } from "react-i18next";
import apiUrls from "src/api";
import { jwtSelector } from "src/common/state/selectors/authSelector";
import {
  getShowMediaPreviewModal,
  closeMediaPreviewModal,
} from "src/common/state/slice/modal/modalSlice";
import "../../statsNew/components/statsNew.css";
import { DownloadIcon, XIcon } from "@heroicons/react/outline";

interface IModalPreviewProps {
  media: IMedia;
  onCloseCb: () => void;
}

export const ModalPreview: React.FC<IModalPreviewProps> = ({ media, onCloseCb }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(getCurrentUser);
  const jwt = useAppSelector(jwtSelector);
  const currentLocale = languageConfig((currentUser?.preferred_language as ILanguage)?.name);

  const modalState = useAppSelector(getShowMediaPreviewModal);
  const modalId = `preview_${media.id}`;
  const isOpen = modalState.show && modalState.id === modalId;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const lastTimeUpdateRef = useRef(0);

  const isMedia360Video = media.media_360_tag === "media360" && media.type === "video";

  const durationSetRef = useRef(false);

  const handleTimeUpdate = useCallback((currentTime: number) => {
    const now = Date.now();
    if (now - lastTimeUpdateRef.current > 250) {
      lastTimeUpdateRef.current = now;
      setVideoCurrentTime(currentTime);
      if (!durationSetRef.current && videoRef.current && videoRef.current.duration > 0) {
        durationSetRef.current = true;
        setVideoDuration(videoRef.current.duration);
      }
    }
  }, []);

  const handleDownload = async () => {
    try {
      const response = await fetch(`${apiUrls.medias.download}/${media.id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok || !response.body) {
        throw new Error("Download failed.");
      }

      const reader = response.body.getReader();
      const chunks = [];
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        if (value) chunks.push(value);
        done = readerDone;
      }

      const blob = new Blob(chunks);
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = media.filename || "downloaded_file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error("❌ Error downloading file:", error);
    }
  };

  const media360Url = useMemo(() => {
    const preset2 = media.s3_url_ffmpeg_preset2 ?? media.s3_url_ffmpeg_preset_2;
    const preset1 = media.s3_url_ffmpeg_preset1 ?? media.s3_url_ffmpeg_preset_1;
    return preset2 ?? preset1 ?? media.s3_url;
  }, [
    media.id,
    media.s3_url,
    media.s3_url_ffmpeg_preset1,
    media.s3_url_ffmpeg_preset2,
    media.s3_url_ffmpeg_preset_1,
    media.s3_url_ffmpeg_preset_2,
  ]);

  const renderOtherMedia = () => {
    if (media.type === "image") {
      return <img className="object-contain w-full h-full" src={media.s3_url} alt="image-media" />;
    }
    if (media.type === "video") {
      return (
        <video controls autoPlay className="w-full h-full">
          <source src={media.s3_url} type="video/mp4" />
        </video>
      );
    }
    if (media.type === "audio") {
      return (
        <div className="flex items-center justify-center w-full h-full">
          <audio id="myAudio" className="w-full max-w-2xl" controls autoPlay>
            <source src={media.s3_url} />
          </audio>
        </div>
      );
    }
    if (media.type === "pdf") {
      return <p>coming soon(tm)</p>;
    }
    if (media.type === "model3d") {
      return (
        <Model3dViewerWeb
          modelUrl={media.s3_url}
          className="w-full h-full"
          canvasClassName="rounded-2xl"
        />
      );
    }
    if (media.type === "avatar") {
      return (
        <img
          className="object-contain w-full h-full"
          src={media.thumbnail_s3_url}
          alt="image-media"
        />
      );
    }
    return (
      <img
        className="object-contain h-full w-full"
        src="https://d1nhio0ox7pgb.cloudfront.net/_img/g_collection_png/standard/512x512/link_broken.png"
        alt="Broken link"
      />
    );
  };

  const handleClose = useCallback(() => {
    dispatch(closeMediaPreviewModal(modalId));
    onCloseCb?.();
  }, [dispatch, modalId, onCloseCb]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) {
    return null;
  }

  const mediaTypeInfo = prettifyMediaType({ type: media.type, media_360_tag: media.media_360_tag });

  return createPortal(
    <div
      className="stats-modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="media-preview-title"
    >
      <div
        className="stats-modal-content w-full max-w-5xl h-[80vh] max-h-[800px] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="min-w-0 flex-1">
              <h2
                id="media-preview-title"
                className="text-lg font-semibold text-gray-900 truncate"
                title={media.name}
              >
                {media.name}
              </h2>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-sm text-gray-500">
                  {prettifyFileSizeInBytes(Number(media.size_in_bytes), currentLocale)}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  {mediaTypeInfo}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
            >
              <DownloadIcon className="h-4 w-4" strokeWidth={2} />
              <span className="text-sm font-medium">{t("general.download")}</span>
            </button>

            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={t("general.close")}
            >
              <XIcon className="h-6 w-6" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-4">
          <div className="relative w-full h-full bg-black rounded-xl overflow-hidden">
            {media.media_360_tag === "video180stereo" && media.type !== "model3d" && (
              <Player360
                mediaUrl={media.s3_url}
                mediaType={media.type as "image" | "video"}
                mediaTag360={media.media_360_tag}
                className="w-full h-full object-contain"
                canvasClassName="rounded-xl"
                disableFullScreen
              />
            )}

            {media.media_360_tag === "media360" && media.type !== "model3d" && (
              <InteractiveViewer360
                key={media.id}
                mediaUrl={media360Url}
                mediaType={isMedia360Video ? "video" : "image"}
                heatmapImageData={null}
                opacity={0}
                videoRef={videoRef}
                onTimeUpdate={isMedia360Video ? handleTimeUpdate : undefined}
                showFullscreenButton
              />
            )}

            {media.media_360_tag !== "video180stereo" &&
              media.media_360_tag !== "media360" &&
              renderOtherMedia()}

            {isMedia360Video && videoDuration > 0 && (
              <div className="absolute bottom-4 left-4 right-4 z-10">
                <VideoControls
                  videoRef={videoRef}
                  currentTime={videoCurrentTime}
                  duration={videoDuration}
                  segmentStart={0}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
